import { useEffect } from "react";
import { Button, Col, Form, Modal, Row } from "antd";
import dayjs from "dayjs";
import { CloseOutlined } from "@ant-design/icons";
import { FaClock, FaUser } from "react-icons/fa6";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { RiExportFill } from "react-icons/ri";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import downloadCsv from "../../../global/utils/downloadCsv";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";

const reportUrl = `${process.env.REACT_APP_API_URL}/api/registers/on-site-reg-report/`;
const optionsUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;

export const RegisterReportForm = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const reportType = Form.useWatch("Type", form);
  const timeType = Form.useWatch("TimeType", form);
  const oemNo = Form.useWatch("OemNo", form);
  const { data: optionsData, loading: optionsLoading } = useGet(optionsUrl);
  const { data: dealerData } = useGet(
    oemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${oemNo}/dealers/`
      : null,
  );
  const { postData, loading } = usePost(reportUrl);

  const oemOptions = getModifiedResponse(optionsData, "oems");
  const dealerOptions = (dealerData?.data || []).map((dealer) => ({
    label: dealer.name,
    value: dealer.value,
  }));

  const showOemRow = reportType === "oem" || reportType === "dealer";
  const showDealerRow = reportType === "dealer";
  const showMonthRow = timeType === "month";
  const showTimeRangeRow = timeType === "time";

  useEffect(() => {
    if (reportType !== "dealer") {
      form.setFieldValue("DealerNo", undefined);
    }
    if (reportType === "all") {
      form.setFieldValue("OemNo", undefined);
    }
  }, [form, reportType]);

  useEffect(() => {
    if (timeType !== "month") {
      form.setFieldValue("Month", undefined);
    }
    if (timeType !== "time") {
      form.setFieldsValue({ StartTime: undefined, EndTime: undefined });
    } else {
      form.setFieldValue("Month", undefined);
    }
  }, [form, timeType]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async (values) => {
    const payload = {
      Type: values.Type,
      TimeType: values.TimeType,
    };

    if (values.Type === "oem" || values.Type === "dealer") {
      payload.OemNo = values.OemNo;
    }
    if (values.Type === "dealer") {
      payload.DealerNo = values.DealerNo;
    }
    if (values.TimeType === "month") {
      payload.Month = values.Month.format("YYYY-MM");
    }
    if (values.TimeType === "time") {
      payload.StartTime = values.StartTime.format("YYYY-MM-DD HH:mm");
      payload.EndTime = values.EndTime.format("YYYY-MM-DD HH:mm");
    }

    const result = await postData(payload);
    if (result?.success === false) {
      openErrorNotification(
        result.message === "No data" ? "No Data Found" : result.message,
      );
      return;
    }
    if (typeof result !== "string" || !result.trim()) {
      openErrorNotification("No Data Found");
      return;
    }

    downloadCsv(
      result,
      window.location,
      `On Site Register Report (${dayjs().format("YYYY-MM-DD")})`,
    );
    openConfirmNotification("Success");
    handleClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center bg-gray-200 p-2 rounded-md">
          <RiExportFill className="text-[20px] text-black pl-1" />
          <span className="text-[14px] font-[Poppins] ml-2">
            On Site Register Report
          </span>
        </div>
      }
      open={open}
      closable={false}
      onCancel={handleClose}
      footer={null}
      width={580}
      centered
      destroyOnClose
    >
      <Form
        validateTrigger="onChange"
        form={form}
        initialValues={{ Type: "all", TimeType: "all" }}
        onFinish={handleSubmit}
        layout="vertical"
        autoComplete="off"
      >
        <div className="overflow-hidden border bg-white rounded-[4px]">
          <div className="flex items-center bg-gray-200 p-2 rounded-[4px]">
            <FaUser className="text-black ml-1" />
            <span className="text-[14px] ml-2">Dealer</span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <CustomSelect
                  label="Select Type"
                  name="Type"
                  options={[
                    { label: "All", value: "all" },
                    { label: "OEM", value: "oem" },
                    { label: "Dealer", value: "dealer" },
                  ]}
                  placeholder="Please select"
                  required
                  allowClear={false}
                />
              </Col>
              {showOemRow && (
                <Col span={24}>
                  <CustomSelect
                    label="OEM"
                    name="OemNo"
                    options={oemOptions}
                    placeholder="Please select"
                    loading={optionsLoading}
                    required
                    onChange={() => form.setFieldValue("DealerNo", undefined)}
                  />
                </Col>
              )}
              {showDealerRow && (
                <Col span={24}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={dealerOptions}
                    placeholder="Please select"
                    required
                    disabled={!oemNo}
                  />
                </Col>
              )}
            </Row>
          </div>
        </div>

        <div className="overflow-hidden border bg-white rounded-[4px] mt-2.5">
          <div className="flex items-center bg-gray-200 p-2 rounded-[4px]">
            <FaClock className="text-black" />
            <span className="text-[14px] ml-2">Registration Time</span>
          </div>
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[0, 0]}>
              <Col span={24}>
                <CustomSelect
                  label="Select Type"
                  name="TimeType"
                  options={[
                    { label: "All", value: "all" },
                    { label: "Month", value: "month" },
                    { label: "Time", value: "time" },
                  ]}
                  placeholder="Please select"
                  required
                  allowClear={false}
                />
              </Col>
              {showMonthRow && (
                <Col span={24}>
                  <CustomDateTimePicker
                    name="Month"
                    label="Month"
                    placeholder="Please select"
                    picker="month"
                    required
                  />
                </Col>
              )}
              {showTimeRangeRow && (
                <>
                  <Col span={24}>
                    <CustomDateTimePicker
                      name="StartTime"
                      label="Start Time"
                      placeholder="Please select"
                      required
                      disableFuture
                    />
                  </Col>
                  <Col span={24}>
                    <CustomDateTimePicker
                      name="EndTime"
                      label="End Time"
                      placeholder="Please select"
                      required
                      disableFuture
                      dependencies={["StartTime"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const start = getFieldValue("StartTime");
                            if (!value || !start || !value.isBefore(start)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("End time cannot be before start time"),
                            );
                          },
                        }),
                      ]}
                    />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>

        <div className="flex justify-center space-x-2 mt-2.5">
          <Button
            type="primary"
            className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
            onClick={() => form.submit()}
            loading={loading}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            <span>Apply</span>
          </Button>
          <Button className="reset-button" onClick={handleClose}>
            <CloseOutlined />
            <span>Cancel</span>
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
